import { Button } from 'primereact/button';
import { Chart } from 'primereact/chart';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Divider } from 'primereact/divider';
import { TabPanel, TabView } from 'primereact/tabview';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Chart as ChartJS } from 'chart.js';
import logoUrl from '../../../assets/checkmark.svg';
import { CaluculateMetricValue, metricValueFormat } from '../../../utils/metricValueFormat';
import { monthNames } from '../../../utils/typeConstants';
import MetricGoal from './MetricGoal';
import { selectMetricChartHandleSave } from '../../../pages/login/authSlice';
import { hideLoader, showLoader } from '../../../components/loadingSpinner/loadingspinner';
import './MetricCharts.scss';

ChartJS.register(ChartDataLabels);

/* ────────────────────────────────────────────────────────────────────────
 * CONFIRMED COLOR SPEC — index-based, 7 slots (metricIndex 0-6)
 * ────────────────────────────────────────────────────────────────────────
 * scorecard_calculation_rule_id === 2 (tiered):
 * - EXCEEDS_COLORS: the Exceeds dashed line, AND any bar at/above the
 *   Exceeds threshold, AND the DataTable Exceeds column text.
 * - MEETS_COLORS: the Meets dashed line, AND any bar at/above Meets but
 *   below Exceeds, AND the DataTable Meets column text.
 * - BELOW_MEETS_COLORS: any bar below Meets.
 *
 * scorecard_calculation_rule_id === 1 (single target):
 * - Target line: fixed '#2AA154' (NOT index-based).
 * - Bar color: metric_attained true -> EXCEEDS_COLORS, false ->
 *   BELOW_MEETS_COLORS (reusing item.metric_attained directly rather than
 *   recomputing the direction-aware comparison ourselves — that flag is
 *   already computed correctly by the backend).
 * ──────────────────────────────────────────────────────────────────────── */
const EXCEEDS_COLORS = ['#FF9933', '#0CD9BF', '#499DFF', '#AC53FF', '#EB6BE5', '#FF568B', '#FF5C5E'];
const MEETS_COLORS = ['#E26A43', '#00B6B2', '#2A78D3', '#8913CD', '#C848C2', '#E83683', '#C42629'];
const BELOW_MEETS_COLORS = ['#B14F2F', '#089886', '#2B5B93', '#601AA1', '#7D1F78', '#C11E52', '#8B1214'];
const RULE1_TARGET_LINE_COLOR = '#2AA154';

/* ────────────────────────────────────────────────────────────────────────
 * CHART BACKGROUND COLOR SPEC — rule 2 only, index-based (8 slots this
 * time — the extra 8th slot vs. the 7-slot palettes above appears to be a
 * neutral gray fallback; pickByIndex's modulo handles any array length
 * generically either way).
 * ────────────────────────────────────────────────────────────────────────
 * Three vertical bands, split by the Meets/Exceeds line positions:
 * - EXCEEDS_BG_COLORS: band above the Exceeds line.
 * - MEETS_BG_COLORS: band between Meets and Exceeds.
 * - BELOW_MEETS_BG_COLORS: band below the Meets line.
 * Rule 1's background logic (2-band green/red split around Target) is
 * unchanged — these 3 new arrays only apply when isMultiGoal.
 * ──────────────────────────────────────────────────────────────────────── */
const EXCEEDS_BG_COLORS = ['#FFDCB3', '#BFF7F0', '#B5DAFF', '#E3C7FF', '#FACDFA', '#FAC5DA', '#FAC5C6', '#DADADA'];
const MEETS_BG_COLORS = ['#F3B89A', '#8EE2DF', '#8DBCED', '#C890EB', '#E8A7E7', '#F6A1C4', '#F6A1A2', '#C1C1C1'];
const BELOW_MEETS_BG_COLORS = ['#E0AE92', '#91D6CE', '#8DB0D4', '#B792D9', '#CA96C9', '#E697B1', '#E69798', '#989898'];

/** Picks palette[index % palette.length]. If `index` isn't supplied at
 *  all, it's treated as 0 — "assume metric index 0", not a separate fixed
 *  fallback color. */
function pickByIndex(palette: string[], index: number | undefined): string {
    const effectiveIndex = index ?? 0;
    return palette[effectiveIndex % palette.length] ?? palette[0];
}

/** Direction-aware "did this value clear that threshold" check, matching
 *  the same ascending/descending convention already used elsewhere in
 *  this file (metric_direction_id === 1 -> ascending/higher-is-better,
 *  2 -> descending/lower-is-better). Used for rule 2's Meets/Exceeds
 *  region resolution — rule 1 uses item.metric_attained directly instead
 *  since that flag already encodes this same logic from the backend. */
function clearsThreshold(value: number, threshold: number, directionId: number): boolean {
    return directionId === 2 ? value <= threshold : value >= threshold;
}

interface MetricChartProps {
    coachingMetric: any;
    coachingScorecardId: number;
    // onMetricDialogHide: () => void;
    receiverName: string;
    inputMonth?: number;
    inputYear?: number;
    metricId?: number;
    // via?: number;
    // gameplanId?: number;
}

const MetricChart = (props: MetricChartProps) => {
    const metricChartHandleSave = useSelector(selectMetricChartHandleSave);
    const [basicData, setBasicData] = useState<any>();
    const [activeIndex, setActiveIndex] = useState<number>(0);
    const [label, setLabel] = useState<any>([]);
    const [metricData, setMetricData] = useState<any>([]);
    const [coachingMetricData, setCoachingMetricData] = useState<any>([]);
    const [showTLCpmment, setShowTLCpmment] = useState<boolean>(false);
    const [showAddGoal, setShowAddGoal] = useState<boolean>(false);
    const [saveClicked, setSaveClicked] = useState<boolean>(false);
    // const [coachingMetricId, setCoachingMetricId] = useState<number>();
    const [coreValueId, setCoreValueId] = useState<number>(0);
    const [formChanged, setFormChanged] = useState<boolean>(false);
    const [currentMetricDirection, setCurrentMetricDirection] = useState<number>(1);
    const [minValue, setMinValue] = useState<number>(0);
    const [maxValue, setMaxValue] = useState<number>(100);
    const [metricValueColors, setMetricValueColors] = useState<string[]>([]);

    const handleFormChanged = (event: boolean) => {
        setFormChanged(event);
    };

    // const handleSaveClicked = (coachingMetricId: number) => {
    //     setCoachingMetricId(coachingMetricId);
    // };
    const handleCoreValueId = (coreValueId: number) => {
        setCoreValueId(coreValueId);
    };
    const [todoVisible, setTodoVisible] = useState(false);
    const [shoutoutVisible, setShoutoutVisible] = useState(false);

    useEffect(() => {
        if (props?.coachingMetric?.data?.length > 0) {
            const array: any = [];
            props?.coachingMetric?.data[0].metrics.forEach((item: any) => {
                const obj = {
                    label: item.label,
                    metricId: item.metric_id,
                    metricDirectionId: item?.monthlyMetrics[0]?.metric_direction_id,
                };
                array.push(obj);
            });
            setLabel(array);
            setMetricData(props?.coachingMetric?.data[0]?.metrics);
            if (props?.metricId !== undefined) {
                const currentMetricData = props?.coachingMetric?.data[0]?.metrics.find(
                    (item: any) => item.metric_id === props?.metricId
                );
                const foundIndex = array.findIndex((item: any) => {
                    return item.metricId === props?.metricId;
                });
                setCoachingMetricData(currentMetricData?.monthlyMetrics);
                renderMetrics(currentMetricData?.monthlyMetrics, foundIndex);
                calculateMinMaxValues(currentMetricData?.monthlyMetrics);
                setCurrentMetricDirection(currentMetricData?.monthlyMetrics[0]?.metric_direction_id);
                setActiveIndex(foundIndex);
            } else {
                setCoachingMetricData(props?.coachingMetric?.data[0]?.metrics[0].monthlyMetrics);
                renderMetrics(props?.coachingMetric?.data[0]?.metrics[0].monthlyMetrics, 0);
                calculateMinMaxValues(props?.coachingMetric?.data[0]?.metrics[0]?.monthlyMetrics);
            }
        } else {
            // No metrics for the current filter selection (e.g. a
            // scorecard change that comes back with an empty `data`
            // array). Previously there was no else branch at all, so
            // label/metricData/coachingMetricData/basicData just kept
            // whatever they were from the PREVIOUS selection — nothing
            // ever told them to clear, which is why the chart/table kept
            // showing old data even though props.coachingMetric itself had
            // genuinely updated.
            setLabel([]);
            setMetricData([]);
            setCoachingMetricData([]);
            setBasicData(undefined);
            setActiveIndex(0);
        }
    }, [props?.coachingMetric]);

    const calculateMinMaxValues = (individual: any) => {
        const metricValues = individual?.map((item: any) =>
            CaluculateMetricValue(Number(item.metric_value) || 0, item.metric_type_name)
        );
        const targetValues = individual?.map((item: any) =>
            CaluculateMetricValue(Number(item.target) || 0, item.metric_type_name)
        );
        // Meets/Exceeds tier values also have to factor into the range —
        // otherwise a tiered metric's Exceeds line (typically HIGHER than
        // target/metric_value for an ascending metric) sits above the
        // computed max and renders clipped at the top of the chart. Same
        // tier_level convention used everywhere else in this file
        // (1 = Meets, 2 = Exceeds).
        const tierValues = individual?.flatMap((item: any) => {
            const meetsTier = item?.targets?.find((t: any) => t.tier_level === 1);
            const exceedsTier = item?.targets?.find((t: any) => t.tier_level === 2);
            return [meetsTier?.target, exceedsTier?.target]
                .filter((v: any) => v != null)
                .map((v: any) => CaluculateMetricValue(Number(v), item.metric_type_name));
        }) ?? [];

        const allValues = [...metricValues, ...targetValues, ...tierValues];
        const minY = Math.min(...allValues, 0); // Ensure 0 is included
        const maxY = Math.max(...allValues);

        setMinValue(minY);
        setMaxValue(maxY + (maxY / 8));
    };
    const basicOptions = {
        maintainAspectRatio: false,
        aspectRatio: 0.9,
        responsive: true,
        // Headroom above the plot area for the datalabels below, which
        // render ABOVE each bar/point (align: 'top', offset: -2) — without
        // this there's nothing reserved above the plot area, so a label on
        // the tallest bar has nowhere to draw and clips right at the
        // canvas's own top edge. This is the actual "graph going out of
        // the page on top side" issue.
        layout: {
            padding: {
                top: 24,
            },
        },
        plugins: {
            legend: { position: 'top', display: false },
            datalabels: {
                display: true,
                anchor: 'end',
                align: 'top',
                offset: -2,
                formatter: (value: any) => {
                    return value;
                },
                font: { weight: 'bold' },
                color: (context: any) => {
                    const idx = context.dataIndex;
                    return metricValueColors[idx];
                },
            },
        },
        scales: {
            x: {
                display: true,
                grid: {
                    color: 'transparent',
                    drawBorder: true,
                    borderWidth: 3,
                    borderColor: '#000000',
                },
                title: {
                    display: true,
                    text: 'Months',
                },
                ticks: {
                    color: '#495057',
                },
            },
            y: {
                display: true,
                grid: {
                    color: 'transparent',
                    drawBorder: true,
                    borderWidth: 3,
                    borderColor: '#000000',
                },
                title: {
                    display: true,
                    text: 'Metric Value',
                },
                ticks: {
                    beginAtZero: true,
                    color: '#495057',
                    autoskip: false, // Show all ticks
                    callback(value: any, index: number, ticks: any) {
                        const values = ticks.map((t: any) => t.value);
                        const min = Math.min(...values);
                        const max = Math.max(...values);
                        if (value === min) {
                            return ['Min.', value.toFixed(2)]; // Show min value with 2 decimal places
                        } else if (value === max) {
                            return ['Max.', value.toFixed(2)]; // Show max value with 2 decimal places
                        } else {
                            return ''; // hide other ticks
                        }
                    },
                },
                min: minValue, // Set minimum value for y-axis
                max: maxValue, // Set maximum value for y-axis (adjust as needed)
            },
        },
    };

    // const handleCancel = () => {
    //     setShowAddGoal(false);
    //     props.onMetricDialogHide();
    // };

    const handleSave = () => {
        setSaveClicked(true);
    };

    const handleAddGoal = () => {
        if (showTLCpmment) setShowAddGoal(true);
        else toast.error('Author Comment is required \n');
    };

    const targetMetricValueBodyTemplate = (rowData: any, type: string) => {
        console.log(rowData)
        let displayimg = '--';
        let targetValue = '--';
        let metricValue = '--';
        let monthValue = '--';
        let meetsValue = '--';
        let exceedsValue = '--';
        let metricValueColor = 'black';

        if (rowData && type === 'target') {
            if (rowData.target) {
                targetValue = metricValueFormat(rowData.target, rowData.metric_type_name);
            }
        } else if (rowData && type === 'meets') {
            // "Meets" isn't a flat field on rowData — it lives inside the
            // `targets` array, keyed by tier_level (1 = Meets, 2 = Exceeds),
            // same convention used throughout the tiered-scorecard work
            // elsewhere in this app.
            const meetsTier = rowData?.targets?.find((t: any) => t.tier_level === 1);
            if (meetsTier?.target != null) {
                meetsValue = metricValueFormat(meetsTier.target, rowData.metric_type_name);
            }
        } else if (rowData && type === 'exceeds') {
            const exceedsTier = rowData?.targets?.find((t: any) => t.tier_level === 2);
            if (exceedsTier?.target != null) {
                exceedsValue = metricValueFormat(exceedsTier.target, rowData.metric_type_name);
            }
        } else if (rowData && type === 'metric_value') {
            if (rowData.metric_value) {
                metricValue = metricValueFormat(rowData.metric_value, rowData.metric_type_name);
                const metricValueInNumber = CaluculateMetricValue(
                    rowData.metric_value,
                    rowData.metric_type_name
                );
                const direction = rowData.metric_direction_id;
                const meetsTier = rowData?.targets?.find((t: any) => t.tier_level === 1);
                const exceedsTier = rowData?.targets?.find((t: any) => t.tier_level === 2);

                if (meetsTier?.target != null || exceedsTier?.target != null) {
                    // Tiered (rule 2) row — 3-way region color, FIXED (not
                    // index-based, unlike the bar/line/header colors above):
                    // Exceeds #2AA154, Meets #5279DD, Below Meets #E23636.
                    metricValueColor = '#E23636'; // default: below Meets
                    if (
                        exceedsTier?.target != null
                        && clearsThreshold(
                            metricValueInNumber,
                            CaluculateMetricValue(exceedsTier.target, rowData.metric_type_name),
                            direction
                        )
                    ) {
                        metricValueColor = '#2AA154';
                    } else if (
                        meetsTier?.target != null
                        && clearsThreshold(
                            metricValueInNumber,
                            CaluculateMetricValue(meetsTier.target, rowData.metric_type_name),
                            direction
                        )
                    ) {
                        metricValueColor = '#5279DD';
                    }
                } else {
                    // Rule 1 (no tiers on this row) — unchanged from before.
                    const targetValueInNumber = CaluculateMetricValue(rowData.target, rowData.metric_type_name);
                    if (direction === 1) {
                        metricValueColor = metricValueInNumber >= targetValueInNumber ? '#2AA154' : '#C90370';
                    } else if (direction === 2) {
                        metricValueColor = metricValueInNumber <= targetValueInNumber ? '#2AA154' : '#C90370';
                    }
                }
            }
        } else if (rowData && type === 'label') {
            displayimg = 'metric' + rowData.metric_index;
        } else if (rowData && type === 'month') {
            monthValue = monthNames[rowData.month] + ' ' + rowData.year;
        }

        return (
            <>
                {type === 'month' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        {/* <span
                            style={{
                                height: '0.625rem',
                                width: '0.625rem',
                                borderRadius: '50%',
                            }}
                        /> */}
                        <span style={{ fontSize: '15px', fontWeight: 'bold' }} className="image-text">
                            {monthValue}
                        </span>
                    </div>
                )}
                {type === 'target' && (
                    <span className="image-text" style={{ color: '#1A5AA5', fontSize: '15px' }}>
                        {targetValue}
                    </span>
                )}
                {type === 'meets' && (
                    <span className="image-text" style={{ color: pickByIndex(MEETS_COLORS, activeIndex), fontSize: '15px' }}>
                        {meetsValue}
                    </span>
                )}
                {type === 'exceeds' && (
                    <span className="image-text" style={{ color: pickByIndex(EXCEEDS_COLORS, activeIndex), fontSize: '15px' }}>
                        {exceedsValue}
                    </span>
                )}
                {type === 'metric_value' && (
                    <span className="image-text" style={{ fontSize: '15px', color: metricValueColor }}>
                        {metricValue}
                    </span>
                )}
                {rowData.metric_attained && type === 'metric_attained' && (
                    <img className="justify-content-left " src={logoUrl} alt="Empower" />
                )}
                {rowData && type === 'label' && (
                    <div className="flex align-items-center gap-2">
                        <span className={displayimg} />
                        <span className="imagespacing">{rowData.label}</span>
                    </div>
                )}
            </>
        );
    };
    const chartBackgroundPlugin = {
        id: 'customBackgroundColor',
        beforeDraw: (chart: any) => {
            const { ctx, chartArea: area, scales } = chart;
            const isMultiGoal = props?.coachingMetric?.data?.[0]?.scorecard_calculation_rule_id === 2;

            // Clear existing background
            ctx.save();
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(area.left, area.top, area.right - area.left, area.bottom - area.top);
            ctx.restore();

            if (isMultiGoal) {
                // Rule 2 — 3 vertical bands split by the Meets/Exceeds line
                // positions, index-based colors. dataset[1] is always Meets
                // and dataset[2] is always Exceeds (see referenceLineDatasets
                // in renderMetrics — Meets is placed first specifically so
                // this stays true).
                const meetsValue = chart.data.datasets[1]?.data?.[0];
                const exceedsValue = chart.data.datasets[2]?.data?.[0];
                if (meetsValue == null || exceedsValue == null) return;

                const yMeets = scales.y.getPixelForValue(meetsValue);
                const yExceeds = scales.y.getPixelForValue(exceedsValue);
                // Ascending: Exceeds > Meets numerically, so Exceeds sits at
                // the smaller pixel Y (visually higher). Descending: Exceeds
                // < Meets numerically, so Exceeds sits at the LARGER pixel Y
                // (visually lower) instead — sort by pixel value rather than
                // assume which of the two is physically on top.
                const yTop = Math.min(yMeets, yExceeds);
                const yBottom = Math.max(yMeets, yExceeds);

                const exceedsBgColor = pickByIndex(EXCEEDS_BG_COLORS, activeIndex);
                const meetsBgColor = pickByIndex(MEETS_BG_COLORS, activeIndex);
                const belowMeetsBgColor = pickByIndex(BELOW_MEETS_BG_COLORS, activeIndex);

                // Descending metric (lower is better): the visual stacking
                // reverses — Below Meets colors the TOP band (values worse
                // than Meets sit visually higher, since higher numbers
                // always render higher regardless of direction), Exceeds
                // colors the BOTTOM band. Middle (Meets) band is unchanged
                // either way. Ascending keeps the original top=Exceeds,
                // bottom=Below Meets assignment.
                const isDescending = currentMetricDirection === 2;
                const topBandColor = isDescending ? belowMeetsBgColor : exceedsBgColor;
                const bottomBandColor = isDescending ? exceedsBgColor : belowMeetsBgColor;

                // Top band
                ctx.save();
                ctx.fillStyle = topBandColor;
                ctx.fillRect(area.left, area.top, area.right - area.left, yTop - area.top);
                ctx.restore();

                // Between Meets and Exceeds (unchanged either direction)
                ctx.save();
                ctx.fillStyle = meetsBgColor;
                ctx.fillRect(area.left, yTop, area.right - area.left, yBottom - yTop);
                ctx.restore();

                // Bottom band
                ctx.save();
                ctx.fillStyle = bottomBandColor;
                ctx.fillRect(area.left, yBottom, area.right - area.left, area.bottom - yBottom);
                ctx.restore();
            } else {
                // Rule 1 — UNCHANGED from before: 2-band green/red split
                // around the Target line (dataset[1]).
                const threshold = chart.data.datasets[1]?.data?.[0]; // assuming threshold is consistent
                if (threshold == null) return;

                const yThreshold = scales.y.getPixelForValue(threshold);

                // Draw background above threshold (e.g., light green)
                ctx.save();
                ctx.fillStyle = currentMetricDirection === 1 ? '#d5eedd' : '#efd8e1'; // green above threshold
                ctx.fillRect(area.left, area.top, area.right - area.left, yThreshold - area.top);
                ctx.restore();

                // Draw background below threshold (e.g., light red)
                ctx.save();
                ctx.fillStyle = currentMetricDirection === 1 ? '#efd8e1' : '#d5eedd'; // red below threshold
                ctx.fillRect(area.left, yThreshold, area.right - area.left, area.bottom - yThreshold);
                ctx.restore();
            }
        },
    };
    const renderMetrics = (individual: any, metricIndex: number) => {
        const metric = individual?.map((item: any) =>
            CaluculateMetricValue(item.metric_value, item.metric_type_name)
        );
        const thresholdData = individual?.map((item: any) =>
            CaluculateMetricValue(item.target, item.metric_type_name)
        );
        // Only used when scorecard_calculation_rule_id === 2 — see
        // isMultiGoal below. Pulled from each month's own `targets` array
        // (tier_level 1 = Meets, 2 = Exceeds), same convention used for the
        // targetMetricValueBodyTemplate table columns above. `null` (not
        // 0/undefined) for a month missing that tier, so the line dataset
        // naturally shows a gap there rather than dropping to 0.
        const meetsData = individual?.map((item: any) => {
            const meetsTier = item?.targets?.find((t: any) => t.tier_level === 1);
            return meetsTier?.target != null
                ? CaluculateMetricValue(meetsTier.target, item.metric_type_name)
                : null;
        });
        const exceedsData = individual?.map((item: any) => {
            const exceedsTier = item?.targets?.find((t: any) => t.tier_level === 2);
            return exceedsTier?.target != null
                ? CaluculateMetricValue(exceedsTier.target, item.metric_type_name)
                : null;
        });
        const labels = individual?.map((item: any) => monthNames[item.month] + ' ' + item.year);

        const isMultiGoal = props?.coachingMetric?.data?.[0]?.scorecard_calculation_rule_id === 2;

        // Bar colors — replaces the old gradient-based backgroundColors.
        // Rule 1: item.metric_attained (already direction-aware, computed
        // by the backend) picks EXCEEDS_COLORS vs BELOW_MEETS_COLORS.
        // Rule 2: which of the 3 regions the bar's own value falls into —
        // below Meets / Meets-to-Exceeds / at-or-above Exceeds — using
        // clearsThreshold for the same direction-aware comparison, applied
        // to each month's own Meets/Exceeds tier values (glidepath-aware,
        // same as meetsData/exceedsData above).
        const backgroundColors = individual.map((item: any) => {
            if (!isMultiGoal) {
                return item.metric_attained
                    ? pickByIndex(EXCEEDS_COLORS, metricIndex)
                    : pickByIndex(BELOW_MEETS_COLORS, metricIndex);
            }

            const value = CaluculateMetricValue(item.metric_value, item.metric_type_name);
            const direction = item.metric_direction_id;
            const meetsTier = item?.targets?.find((t: any) => t.tier_level === 1);
            const exceedsTier = item?.targets?.find((t: any) => t.tier_level === 2);

            if (exceedsTier?.target != null) {
                const exceedsVal = CaluculateMetricValue(exceedsTier.target, item.metric_type_name);
                if (clearsThreshold(value, exceedsVal, direction)) {
                    return pickByIndex(EXCEEDS_COLORS, metricIndex);
                }
            }
            if (meetsTier?.target != null) {
                const meetsVal = CaluculateMetricValue(meetsTier.target, item.metric_type_name);
                if (clearsThreshold(value, meetsVal, direction)) {
                    return pickByIndex(MEETS_COLORS, metricIndex);
                }
            }
            return pickByIndex(BELOW_MEETS_COLORS, metricIndex);
        });

        const metricValueColors = individual.map((item: any) => {
            const value = CaluculateMetricValue(item.metric_value, item.metric_type_name);
            const target = CaluculateMetricValue(item.target, item.metric_type_name);
            const direction = item.metric_direction_id;
            if (item.metric_value === null || item.metric_value === undefined) return 'black'; // Default color for null values
            if (direction === 1) {
                return value >= target ? '#2AA154' : '#C90370';
            } else if (direction === 2) {
                return value <= target ? '#2AA154' : '#C90370';
            }
            return 'black'; // Fallback color
        });

        setMetricValueColors(metricValueColors);

        // Rule 2 (tiered scorecards): 2 dotted reference lines (Meets +
        // Exceeds) instead of the single Target line. Rule 1: unchanged
        // structurally, single Target line, now fixed RULE1_TARGET_LINE_COLOR
        // per the confirmed spec (was 'blue' before).
        // Meets is placed FIRST so it lands at dataset index 1 — same
        // position Target occupied before — keeping chartBackgroundPlugin's
        // `chart.data.datasets[1]` threshold lookup above working correctly
        // without needing to touch that plugin at all.

        const referenceLineDatasets = isMultiGoal
            ? [
                {
                    label: 'Meets',
                    data: meetsData,
                    type: 'line',
                    fill: false,
                    borderColor: pickByIndex(MEETS_COLORS, metricIndex),
                    borderWidth: 2,
                    borderDash: [5, 5],
                    // Stepped rather than a smooth diagonal: Meets/Exceeds
                    // genuinely vary per month (glidepath-adjusted), so
                    // without this the line just slopes between differing
                    // values. 'before' steps right at the current month's
                    // own x position. Try 'middle'/'after'/true instead if
                    // the step should land differently relative to the bar.
                    stepped: 'before',
                    datalabels: {
                        display: false,
                    },
                },
                {
                    label: 'Exceeds',
                    data: exceedsData,
                    type: 'line',
                    fill: false,
                    borderColor: pickByIndex(EXCEEDS_COLORS, metricIndex),
                    borderWidth: 2,
                    // Different dash pattern from Meets so the two lines are
                    // distinguishable by shape as well as color, same
                    // convention used for the CoachingGlidePath chart.
                    borderDash: [2, 3],
                    stepped: 'before',
                    datalabels: {
                        display: false,
                    },
                },
            ]
            : [
                {
                    label: 'Target',
                    data: thresholdData,
                    type: 'line',
                    fill: false,
                    borderColor: RULE1_TARGET_LINE_COLOR,
                    borderWidth: 2,
                    borderDash: [5, 5],
                    // Stepped for consistency with the Meets/Exceeds lines
                    // above — doesn't change anything visually for a truly
                    // flat/constant target, but keeps behavior identical if
                    // Target ever varies per month too.
                    stepped: 'before',
                    datalabels: {
                        display: false,
                    },
                },
            ];

        const data = {
            labels,
            datasets: [
                {
                    label: 'Coaching Metric',
                    data: metric,
                    backgroundColor: backgroundColors,
                    barThickness: 'flex',
                    borderRadius: { topLeft: 8, topRight: 8, bottomLeft: 0, bottomRight: 0 },
                    borderSkipped: false,
                    borderWidth: 1,
                    borderColor: 'grey',
                    order: 1,
                    datalabels: {
                        display: true,
                        font: {
                            weight: 'bold',
                        },
                        formatter: (value: any) => value,
                        anchor: 'end',
                    },
                },
                ...referenceLineDatasets,
            ],
        };

        setBasicData(data);
    };

    const handleTabChange = (e: any) => {
        setFormChanged(false);
        setActiveIndex(e.index);
        const individual = metricData.find((item: any) => item.metric_id === label[e.index].metricId);
        setCoachingMetricData(individual.monthlyMetrics);
        renderMetrics(individual.monthlyMetrics, e.index);
        calculateMinMaxValues(individual.monthlyMetrics);
        setCurrentMetricDirection(individual?.monthlyMetrics[0]?.metric_direction_id);
    };

    const headerBodyTemplate = (header: string, color?: string) => {
        return (
            <span
                style={{ fontSize: '16px', fontWeight: 'bold', paddingLeft: header === 'Month' ? '5px' : '', ...(color ? { color } : {}) }}
            >
                {header}
            </span>
        );
    };

    useEffect(() => {
        if (formChanged === true && metricChartHandleSave === 'true') {
            showLoader();
            setTimeout(() => {
                handleSave();
                hideLoader();
            }, 1000);
        }
    }, [metricChartHandleSave]);

    useEffect(() => {
        if (todoVisible) {
            if (formChanged === true) {
                handleSave();
            }
        }
    }, [todoVisible]);

    // Info shown at the top of the chart, explaining what the bar colors
    // and the reference line(s) mean — reuses colors already hardcoded in
    // renderMetrics above (no new colors invented; the real index-based
    // palette is a separate follow-up). "Met"/"Not Met" reuses the same
    // '#2AA154'/'#C90370' pair already used for metricValueColors, so the
    // legend and the actual rendered label colors can't disagree. The
    // Meets/Exceeds/Target values shown are the LAST month's own value
    // (same "reflects the current milestone" convention used for
    // CoachingGlidePath's lastSession tiers), since Meets/Exceeds can vary
    // per month (glidepath) and there's no single flat value to show
    // otherwise.
    const isMultiGoal = props?.coachingMetric?.data?.[0]?.scorecard_calculation_rule_id === 2;
    const lastMonthData = coachingMetricData?.[coachingMetricData.length - 1];
    const lastMeetsTier = lastMonthData?.targets?.find((t: any) => t.tier_level === 1);
    const lastExceedsTier = lastMonthData?.targets?.find((t: any) => t.tier_level === 2);

    const chartInfoLegend = (
        <div className="d-flex align-items-center flex-wrap" style={{ gap: '16px', marginBottom: '8px' }}>
            {isMultiGoal ? (
                <>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: pickByIndex(MEETS_COLORS, activeIndex) }}>
                        <span className="metricTrendLegend__swatch metricTrendLegend__swatch--meets"
                        // style={{ display: 'inline-block', width: '14px', height: '0', borderTop: `2px dashed ${pickByIndex(MEETS_COLORS, activeIndex)}` }} 
                        />
                        Meets: {lastMeetsTier?.target != null
                            ? metricValueFormat(lastMeetsTier.target, lastMonthData?.metric_type_name)
                            : '--'}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: pickByIndex(EXCEEDS_COLORS, activeIndex) }}>
                        <span className="metricTrendLegend__swatch metricTrendLegend__swatch--exceeds"
                        // style={{ display: 'inline-block', width: '14px', height: '0', borderTop: `1px dashed ${pickByIndex(EXCEEDS_COLORS, activeIndex)}` }}
                        />
                        Exceeds: {lastExceedsTier?.target != null
                            ? metricValueFormat(lastExceedsTier.target, lastMonthData?.metric_type_name)
                            : '--'}
                    </span>
                </>
            ) : (
                <>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: pickByIndex(EXCEEDS_COLORS, activeIndex) }}>
                        <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '2px', backgroundColor: pickByIndex(EXCEEDS_COLORS, activeIndex) }} />
                        Met
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: pickByIndex(BELOW_MEETS_COLORS, activeIndex) }}>
                        <span style={{ display: 'inline-block', width: '10px', height: '10px', borderRadius: '2px', backgroundColor: pickByIndex(BELOW_MEETS_COLORS, activeIndex) }} />
                        Not Met
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: RULE1_TARGET_LINE_COLOR }}>
                        <span style={{ display: 'inline-block', width: '14px', height: '0', borderTop: `2px dashed ${RULE1_TARGET_LINE_COLOR}` }} />
                        Target: {lastMonthData?.target != null
                            ? metricValueFormat(lastMonthData.target, lastMonthData?.metric_type_name)
                            : '--'}
                    </span>
                </>
            )}
        </div>
    );

    return (
        <>  
        <TabView
            activeIndex={activeIndex}
            onTabChange={(e) => {
                if (formChanged === true) {
                    handleSave();
                    setTimeout(() => {
                        handleTabChange(e);
                    }, 1000);
                } else {
                    handleTabChange(e);
                }
            }}
        >
            {label &&
                label.length > 0 &&
                label.map((item: any, i: number) => {
                    return (
                        <TabPanel key={i} header={item?.label}>
                            {item?.metricDirectionId === 2 && (
                                <div className="row">
                                    <span style={{ padding: '20px 0px 0px 100px', color: '#C90370' }}>
                                        This is a Descending Metric. Any Score above target is not desired.
                                    </span>
                                </div>
                            )}
                            <div className="row mt-4">
                                <div className="col-md-6">
                                    {chartInfoLegend}
                                    <div className="container">
                                        <Chart
                                            style={{
                                                height: '250px',
                                                width: '500px',
                                                backgroundColor: '#ffffff',
                                            }}
                                            type="bar"
                                            data={basicData}
                                            options={basicOptions}
                                            plugins={[ChartDataLabels, chartBackgroundPlugin]}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6 ps-0 mx-auto" style={{ zIndex: '1px' }}>
                                    <div className="card border-0 metricprogresstable float-end" style={{ marginLeft: '50px' }}>
                                        <DataTable
                                            style={{
                                                width: '75%',
                                            }}
                                            className="targetMetricValueTable"
                                            value={coachingMetricData}
                                        >
                                            <Column
                                                field="month"
                                                // style={{ width: '50%', backgroundColor: 'transparent' }}
                                                header={headerBodyTemplate('Month')}
                                                body={(rowData) => {
                                                    return targetMetricValueBodyTemplate(rowData, 'month');
                                                }}
                                            />
                                            {props?.coachingMetric?.data?.[0]?.scorecard_calculation_rule_id === 2 && (
                                                    <Column
                                                        field="meets"
                                                        // style={{ width: '12.5%', backgroundColor: 'transparent' }}
                                                        header={headerBodyTemplate('Meets', pickByIndex(MEETS_COLORS, activeIndex))}
                                                        body={(rowData) =>
                                                            targetMetricValueBodyTemplate(rowData, 'meets')
                                                        }
                                                    />
                                            )}
                                            {props?.coachingMetric?.data?.[0]?.scorecard_calculation_rule_id === 2 && (
                                                    <Column
                                                        field="exceeds"
                                                        // style={{ width: '12.5%', backgroundColor: 'transparent' }}
                                                        header={headerBodyTemplate('Exceeds', pickByIndex(EXCEEDS_COLORS, activeIndex))}
                                                        body={(rowData) =>
                                                            targetMetricValueBodyTemplate(rowData, 'exceeds')
                                                        }
                                                    />
                                            )}
                                            {props?.coachingMetric?.data?.[0]?.scorecard_calculation_rule_id !== 2 && (
                                                <Column
                                                    field="target"
                                                    // style={{ width: '25%', backgroundColor: 'transparent' }}
                                                    header={headerBodyTemplate('Target')}
                                                    body={(rowData) =>
                                                        targetMetricValueBodyTemplate(rowData, 'target')
                                                    }
                                                />
                                            )}
                                            
                                            <Column
                                                field="metric_value"
                                                // style={{ width: '25%', backgroundColor: 'transparent' }}
                                                header={headerBodyTemplate('Actual')}
                                                body={(rowData) =>
                                                    targetMetricValueBodyTemplate(rowData, 'metric_value')
                                                }
                                            />
                                        </DataTable>
                                    </div>
                                </div>
                            </div>
                            <div className="flexButtons mt-3">
                            {showTLCpmment ? (
                                ''
                            ) : (
                                <Button
                                    label="New Comment"
                                    onClick={() => setShowTLCpmment(true)}
                                    className="btn-primary mb-2 float-end"
                                />
                            )}
                            </div>
                            <Divider />
                            <MetricGoal
                                saveClicked={saveClicked}
                                showAddGoal={showAddGoal}
                                setSaveClicked={setSaveClicked}
                                setShowAddGoal={setShowAddGoal}
                                setShowTLCpmment={setShowTLCpmment}
                                showTLCpmment={showTLCpmment}
                                recipientId={props.coachingMetric?.data[0]?.employee_number}
                                coachingScorecardId={props.coachingScorecardId}
                                metricId={metricData[activeIndex]?.metric_id}
                                metricName={metricData[activeIndex]?.label}
                                inputMonth={props?.inputMonth}
                                inputYear={props?.inputYear}
                                // onSaveClicked={handleSaveClicked}
                                handleCoreId={handleCoreValueId}
                                receiverName={props.receiverName}
                                toDoVisible={todoVisible}
                                setToDoVisible={setTodoVisible}
                                shoutoutVisible={shoutoutVisible}
                                setShoutoutVisible={setShoutoutVisible}
                                coreValueId={coreValueId}
                                // coachingEntityId={coachingMetricId}
                                formChanged={handleFormChanged}
                            // via={props?.via}
                            />
                            <div className="flexButtons">
                                {showAddGoal ? (
                                    ''
                                ) : (
                                    <Button
                                        label="Add Goal"
                                        onClick={handleAddGoal}
                                        className="btn-secondary me-2"
                                    />
                                )}
                            </div>
                        </TabPanel>
                    );
                })}
        </TabView>
        {props?.coachingMetric?.data?.length === 0 && (
            <div className="text-center mt-4 mb-4 no-data-label">No Data Available</div>
        )}
        </>
    );
};

export default MetricChart;